import { Minimize2 } from "lucide-react";
import { CSSProperties, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { StatusPill } from "nazare-ui/components";
import { PageSection, PageSectionAction } from "nazare-ui/components/page-section";
import { cn } from "nazare-ui/utils";

import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import { SkeletonRow } from "../../../../skeleton-row";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useDocumentTitle } from "../../../../use-document-title";
import { useWorkflowSchedules, type ScheduleSummary } from "../../../../workflows-api";
import styles from "./infra.module.css";

function isInfra(schedule: ScheduleSummary) {
  if (schedule.category) return schedule.category === "infra";
  return !schedule.targets.some((target) => target.kind === "workflow");
}

function nextRun(iso?: string | null) {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

export function WorkflowInfraPage() {
  useDocumentTitle("Infrastructure Schedules");
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "all";
  const type = params.get("type") ?? "all";
  const query = useWorkflowSchedules();
  const all = useMemo(() => (query.data?.schedules ?? []).filter(isInfra), [query.data?.schedules]);
  const types = useMemo(() => Array.from(new Set(all.flatMap((item) => item.targets.map((target) => target.kind).filter(Boolean) as string[]))).sort(), [all]);
  const rows = all.filter((item) => (status === "all" || item.state.toLowerCase() === status) && (type === "all" || item.targets.some((target) => target.kind === type)));
  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const setFilter = useCallback((key: string, value: string) => setParams((current) => { const next = new URLSearchParams(current); if (value === "all") next.delete(key); else next.set(key, value); return next; }, { replace: true }), [setParams]);
  const toolbar = useMemo(() => (
    <div className={styles.filterRow}>
      <div className={styles.tabBar} role="tablist" aria-label="Filter by status">{["all", "enabled", "disabled"].map((value) => <button key={value} type="button" role="tab" aria-selected={status === value} className={cn(styles.tab, status === value && styles.tabActive)} onClick={() => setFilter("status", value)}>{value === "all" ? "All statuses" : value[0].toUpperCase() + value.slice(1)}</button>)}</div>
      {types.length > 1 && <div className={styles.tabBar} role="tablist" aria-label="Filter by type">{["all", ...types].map((value) => <button key={value} type="button" role="tab" aria-selected={type === value} className={cn(styles.tab, type === value && styles.tabActive)} onClick={() => setFilter("type", value)}>{value === "all" ? "All types" : value}</button>)}</div>}
    </div>
  ), [setFilter, status, type, types]);
  useSecondaryHeader(toolbar);
  const enabled = rows.filter((item) => item.state === "ENABLED").length;
  return (
    <div className={styles.page}>
      <PageSection
        className={styles.card}
        style={{ viewTransitionName: "wf-card-infra" } as CSSProperties}
        title="Infrastructure schedules"
        count={query.data ? rows.length : undefined}
        description={query.data ? `${enabled.toLocaleString("en-GB")} enabled` : undefined}
        actions={
          <PageSectionAction asChild aria-label="Minimise to dashboard" title="Minimise to dashboard">
            <Link to="/workflows" viewTransition><Minimize2 aria-hidden="true" /></Link>
          </PageSectionAction>
        }
      >
        <div className={styles.scrollShadowWrapper}>
          <div ref={tableWrapRef} className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th className={styles.th}>Name</th><th className={styles.th}>Type</th><th className={styles.th}>Cron</th><th className={styles.th}>Status</th><th className={`${styles.th} ${styles.thRight}`}>Next run</th></tr></thead>
              <tbody>
                {query.isLoading ? Array.from({ length: 8 }, (_, index) => <SkeletonRow key={index} columns={5} />) : query.error ? (
                  <tr><td className={styles.emptyCell} colSpan={5}>Couldn’t load infrastructure schedules.</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td className={styles.emptyCell} colSpan={5}>No infrastructure schedules match these filters.</td></tr>
                ) : rows.map((schedule) => (
                  <tr key={schedule.name} className={styles.row}><td className={`${styles.td} ${styles.nameCell}`}>{schedule.label ?? schedule.name}</td><td className={styles.td}><span className={styles.typeTag}>{schedule.targets[0]?.kind ?? "Infrastructure"}</span></td><td className={`${styles.td} ${styles.cronCell}`}>{schedule.scheduleExpression}</td><td className={styles.td}><StatusPill tone={schedule.state === "ENABLED" ? "success" : "neutral"}>{schedule.state === "ENABLED" ? "Enabled" : "Disabled"}</StatusPill></td><td className={`${styles.td} ${styles.nextRunCell}`}>{nextRun(schedule.nextFireTime)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </PageSection>
    </div>
  );
}
